import { useState, type FC } from 'react';
import { Col, Row } from 'antd';
import { InputText, Radio, Select, Space } from '@g2u/ui-kit';

import { Condition, type Technician, type WithOptionalDataTest } from 'types';
import { InputEmail, Section } from 'components';
import { addSpacesToNumber } from 'helpers';
import styles from './contacts-fields.module.scss';

type Props = {
  title?: string;
  inline?: boolean;
  technician: Technician | null;
} & WithOptionalDataTest;

export const TechnicianContactsFields: FC<Props> = (props) => {
  const { inline, title, technician } = props;
  const [hasSecondary, setHasSecondary] = useState<boolean>(technician?.second_contact === Condition.Yes);

  return (
    <Section title={title ?? 'Account Contacts'} inline={inline} className={styles.contactsFields}>
      <Space.$ direction={Space.Direction.Vertical}>
        <Row gutter={[32, 8]}>
          <Col xl={{ span: 12 }}>
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small}>
              <Select.$
                initialValue={technician?.contact1_title}
                label="Title: "
                name="contact1_title"
                options={[
                  {
                    label: <>Mr</>,
                    value: 'Mr',
                  },
                  {
                    label: <>Mrs</>,
                    value: 'Mrs',
                  },
                  {
                    label: <>Ms</>,
                    value: 'Ms',
                  },
                  {
                    label: <>Miss</>,
                    value: 'Miss',
                  },
                  {
                    label: <>Dr</>,
                    value: 'Dr',
                  },
                  {
                    label: <>Sir</>,
                    value: 'Sir',
                  },
                  {
                    label: <>Lady</>,
                    value: 'Lady',
                  },
                  {
                    label: <>Attn</>,
                    value: 'Attn',
                  },
                  {
                    label: <>Snr Cons</>,
                    value: 'Snr Cons',
                  },
                ]}
              />

              <InputText.$
                required
                label="First Name:"
                name="contact1_firstname"
                placeholder=""
                initialValue={technician?.contact1_firstname}
                rules={[{ required: true }]}
              />

              <InputText.$
                required
                label="Last Name:"
                name="contact1_surname"
                placeholder=""
                initialValue={technician?.contact1_surname}
                rules={[{ required: true }]}
              />
            </Space.$>
          </Col>

          <Col xl={{ span: 12 }}>
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small}>
              <InputText.$
                name={'contact1_mobile'}
                label="Mobile Number:"
                required={true}
                rules={[
                  { required: true, message: 'Mobile number is required' },
                  { pattern: /^04[0-9\s]*$/, message: 'Incorrect number' },
                  { min: 12, message: 'Must have 10 digits' },
                ]}
                initialValue={addSpacesToNumber(technician?.contact1_mobile, [4, 7])}
                maxLength={12}
                formatter={(val) => addSpacesToNumber(val?.toString(), [4, 7])}
              />
              <InputText.$
                name={'contact1_work'}
                label="Work Number:"
                rules={[
                  { pattern: /^[0-9\s]*$/, message: 'Incorrect number' },
                  { min: 12, message: 'Must have 10 digits' },
                ]}
                initialValue={addSpacesToNumber(technician?.contact1_work, [2, 6])}
                maxLength={12}
                formatter={(val) => addSpacesToNumber(val?.toString(), [2, 6])}
              />
              <InputEmail initialValue={technician?.email} fullWidth={false} required={true} disableEmailCheck />
            </Space.$>
          </Col>
        </Row>

        <Row>
          <Col>
            <Radio.$
              required
              initialValue={technician?.second_contact}
              label="Secondary Contact: "
              name="second_contact"
              options={[
                {
                  label: <>Yes</>,
                  value: Condition.Yes,
                },
                {
                  label: <>No</>,
                  value: Condition.No,
                },
              ]}
              rules={[{ required: true }]}
              onChange={(e) => setHasSecondary(e.target.value === Condition.Yes)}
            />
          </Col>
        </Row>

        {hasSecondary && (
          <Row gutter={[32, 8]}>
            <Col xl={{ span: 12 }}>
              <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small}>
                <Select.$
                  initialValue={technician?.contact2_title}
                  label="Title: "
                  name="contact2_title"
                  options={[
                    {
                      label: <>Mr</>,
                      value: 'Mr',
                    },
                    {
                      label: <>Mrs</>,
                      value: 'Mrs',
                    },
                    {
                      label: <>Ms</>,
                      value: 'Ms',
                    },
                    {
                      label: <>Miss</>,
                      value: 'Miss',
                    },
                    {
                      label: <>Dr</>,
                      value: 'Dr',
                    },
                    {
                      label: <>Sir</>,
                      value: 'Sir',
                    },
                    {
                      label: <>Lady</>,
                      value: 'Sir',
                    },
                    {
                      label: <>Attn</>,
                      value: 'Sir',
                    },
                    {
                      label: <>Snr Cons</>,
                      value: 'Snr Cons',
                    },
                  ]}
                />

                <InputText.$
                  required
                  label="First Name:"
                  name="contact2_firstname"
                  placeholder=""
                  initialValue={technician?.contact2_firstname}
                  rules={[{ required: true }]}
                />

                <InputText.$
                  required
                  label="Last Name:"
                  name="contact2_surname"
                  placeholder=""
                  initialValue={technician?.contact2_surname}
                  rules={[{ required: true }]}
                />
              </Space.$>
            </Col>

            <Col xl={{ span: 12 }}>
              <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small}>
                <InputText.$
                  name={'contact2_mobile'}
                  label="Mobile Number:"
                  required={true}
                  rules={[
                    { required: true, message: 'Mobile number is required' },
                    { pattern: /^04[0-9\s]*$/, message: 'Incorrect number' },
                    { min: 12, message: 'Must have 10 digits' },
                  ]}
                  initialValue={addSpacesToNumber(technician?.contact2_mobile, [4, 7])}
                  maxLength={12}
                  formatter={(val) => addSpacesToNumber(val?.toString(), [4, 7])}
                />
                <InputText.$
                  name={'contact2_work'}
                  label="Work Number:"
                  rules={[
                    { pattern: /^[0-9\s]*$/, message: 'Incorrect number' },
                    { min: 12, message: 'Must have 10 digits' },
                  ]}
                  initialValue={addSpacesToNumber(technician?.contact2_work, [2, 6])}
                  maxLength={12}
                  formatter={(val) => addSpacesToNumber(val?.toString(), [2, 6])}
                />
              </Space.$>
            </Col>
          </Row>
        )}
      </Space.$>
    </Section>
  );
};
